import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  ArrowLeft,
  Check,
  Shield,
  Zap,
  Clock,
  Copy,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Wallet,
  Hash,
  Send,
  Gift,
  Users,
  Share2,
  X,
  Timer,
  Flame
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "@/App";

const Subscription = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  
  const [addresses, setAddresses] = useState(null);
  const [network, setNetwork] = useState("TRC20");
  const [txHash, setTxHash] = useState("");
  const [planType, setPlanType] = useState("monthly"); // "monthly" or "annual"
  const [submitting, setSubmitting] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [paymentStep, setPaymentStep] = useState(1);
  const [referralStats, setReferralStats] = useState(null);
  const [claimingReward, setClaimingReward] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [isFirstPurchase, setIsFirstPurchase] = useState(false);
  const [showBotPromo, setShowBotPromo] = useState(false);
  const [promoCountdown, setPromoCountdown] = useState({ hours: 23, minutes: 59, seconds: 59 });

  useEffect(() => {
    fetchAddresses();
    fetchReferralStats();
    fetchSubscriptionStatus();
  }, []);

  // Show promo popup after 1.5s delay for dramatic effect
  useEffect(() => {
    const promoSeen = sessionStorage.getItem('bot_promo_seen');
    if (!promoSeen) {
      const timer = setTimeout(() => {
        setShowBotPromo(true);
        sessionStorage.setItem('bot_promo_seen', 'true');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Countdown timer for FOMO
  useEffect(() => {
    const stored = localStorage.getItem('bot_promo_end');
    let endTime;
    if (stored) {
      endTime = parseInt(stored);
    } else {
      endTime = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('bot_promo_end', endTime.toString());
    }

    const tick = () => {
      const diff = Math.max(0, endTime - Date.now());
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setPromoCountdown({ hours, minutes, seconds });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data.pricing);
      setIsFirstPurchase(response.data.pricing?.first_purchase_eligible || false);
    } catch (error) {
      console.error("Error fetching subscription status:", error);
    }
  };

  const fetchReferralStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralStats(response.data);
    } catch (error) {
      console.error("Error fetching referral stats:", error);
    }
  };

  const getDisplayPrice = () => {
    if (!pricing) return { price: 30, original: null };
    
    if (planType === "annual") {
      return { 
        price: pricing.annual, 
        original: pricing.monthly * 12,
        savings: pricing.annual_savings,
        discount: pricing.annual_discount_percent
      };
    }
    
    // Monthly with possible first purchase discount
    if (isFirstPurchase && pricing.first_purchase_price) {
      return { 
        price: pricing.first_purchase_price, 
        original: pricing.monthly,
        discount: pricing.first_purchase_discount
      };
    }
    
    return { price: pricing.monthly, original: null };
  };

  const copyReferralLink = () => {
    if (referralStats?.referral_link) {
      navigator.clipboard.writeText(referralStats.referral_link);
      toast.success("Referral link copied!");
    }
  };

  const claimReward = async (rewardId) => {
    setClaimingReward(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/referral/claim-reward?reward_id=${rewardId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reward claimed! Check your email.");
      fetchReferralStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim reward");
    } finally {
      setClaimingReward(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/payments/addresses`);
      setAddresses(response.data);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Address copied to clipboard!");
  };

  const handleSubmitPayment = async () => {
    if (!txHash.trim()) {
      toast.error("Please enter the transaction hash");
      return;
    }

    setSubmitting(true);
    setVerificationResult(null);

    const priceInfo = getDisplayPrice();

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/payments/crypto/submit`,
        {
          tx_hash: txHash.trim(),
          network: network,
          amount: priceInfo.price,
          plan_type: planType,
          is_first_purchase: isFirstPurchase
        },
        {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      setVerificationResult(response.data);

      if (response.data.status === "confirmed") {
        toast.success("Payment verified! Your Pro subscription is now active.");
        // Update user context
        if (user) {
          setUser({
            ...user,
            subscription_status: "active",
            subscription_end_date: response.data.subscription_end
          });
        }
        setTimeout(() => navigate("/dashboard"), 2000);
      } else if (response.data.status === "pending") {
        toast.info("Transaction submitted. Please wait for verification.");
      } else {
        toast.error(response.data.message || "Verification failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payment");
      setVerificationResult({
        status: "error",
        message: error.response?.data?.detail || "Failed to submit payment"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSubscribed = user?.subscription_status === "active";
  const currentAddress = network === "BEP20" 
    ? addresses?.usdt_bep20 
    : addresses?.usdt_trc20;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            <span className="font-bold">Aurex AI</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black mb-4">SUBSCRIPTION</h1>
          <p className="text-muted-foreground">
            Upgrade to Pro for unlimited signals and all timeframes
          </p>
        </div>

        {/* Current Status */}
        <div className="bg-card border border-border p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold mb-1">Current Status</h3>
              <p className="text-muted-foreground text-sm">
                {isSubscribed 
                  ? `Pro active until ${user?.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString() : "N/A"}`
                  : "Free plan with limited trials"}
              </p>
            </div>
            <div className={`px-4 py-2 ${
              isSubscribed 
                ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                : "bg-secondary text-muted-foreground border border-border"
            }`}>
              {isSubscribed ? (
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span className="font-bold uppercase text-sm">Pro Active</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-bold uppercase text-sm">Free</span>
                  <span className="font-mono">({user?.free_trials_remaining || 0} left)</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOMO Popup Modal */}
        {showBotPromo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="bot-promo-popup">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowBotPromo(false)} />
            <div className="relative w-full max-w-lg bg-[#111] border-2 border-amber-500/70 p-6 animate-in fade-in zoom-in-95 duration-300">
              {/* Close */}
              <button
                onClick={() => setShowBotPromo(false)}
                className="absolute top-3 right-3 text-muted-foreground hover:text-white transition-colors"
                data-testid="close-promo-popup"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Hot badge */}
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-orange-400 font-bold text-sm uppercase tracking-wider">Limited Time Offer</span>
              </div>

              <h3 className="text-2xl font-black text-white mb-2">Hybrid Automated MT5 Bot</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Lifetime access to our fully automated trading bot — no recurring fees, ever.
              </p>

              {/* Price */}
              <div className="flex items-center gap-4 mb-4">
                <span className="text-2xl text-muted-foreground line-through font-mono">$699</span>
                <span className="text-4xl font-black text-white font-mono">$299</span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold border border-green-500/30">
                  SAVE $400
                </span>
              </div>

              {/* Countdown */}
              <div className="bg-red-500/10 border border-red-500/30 p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Timer className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-xs font-bold uppercase">Offer expires in:</span>
                </div>
                <div className="flex items-center gap-2 font-mono text-xl text-white font-bold">
                  <span className="bg-red-500/20 px-2 py-1">{String(promoCountdown.hours).padStart(2, '0')}</span>
                  <span className="text-red-400">:</span>
                  <span className="bg-red-500/20 px-2 py-1">{String(promoCountdown.minutes).padStart(2, '0')}</span>
                  <span className="text-red-400">:</span>
                  <span className="bg-red-500/20 px-2 py-1">{String(promoCountdown.seconds).padStart(2, '0')}</span>
                </div>
              </div>

              {/* Includes */}
              <div className="space-y-2 mb-5">
                {["Lifetime license — one time payment", "Complimentary Set Files (worth $100)", "All 5 instruments supported", "Free updates forever"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-sm text-white/80">{f}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => {
                  setShowBotPromo(false);
                  document.getElementById('hybrid-bot-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider rounded-none"
                data-testid="promo-get-now-btn"
              >
                <Zap className="w-4 h-4 mr-2" /> Get It Now — $299
              </Button>
              <p className="text-center text-xs text-muted-foreground mt-2">One-time payment. No subscription required.</p>
            </div>
          </div>
        )}

        {/* Hybrid MT5 Bot - Featured Package (Before regular plans) */}
        <div id="hybrid-bot-section" className="mb-10 relative overflow-hidden" data-testid="hybrid-bot-package">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5 pointer-events-none" />
          <div className="border-2 border-amber-500/60 bg-card p-8 relative">
            {/* Limited Time Badge */}
            <div className="absolute top-0 right-0 bg-amber-500 text-black px-4 py-1.5 font-bold text-xs uppercase tracking-wider">
              Limited Time Offer
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left - Product Info */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-white">Hybrid Automated MT5 Bot</h2>
                    <p className="text-sm text-amber-400 font-medium">Lifetime Access — One Time Purchase</p>
                  </div>
                </div>

                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                  Get the complete Hybrid Automated MT5 Expert Advisor with full strategy implementation. 
                  Trade automatically with our proven algorithm — no recurring fees, lifetime updates included.
                </p>

                <div className="space-y-3 mb-6">
                  {[
                    "Fully automated MT5 Expert Advisor",
                    "Complete hybrid strategy (local + API signals)",
                    "Lifetime license — pay once, use forever",
                    "Complimentary Set Files included (worth $100)",
                    "All supported instruments (XAUUSD, XAGUSD, EURUSD, AUDUSD, USDJPY)",
                    "Free lifetime updates & improvements",
                    "Priority support channel"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <span className="text-sm text-white/90">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Pricing & Countdown */}
              <div className="text-center">
                <div className="bg-secondary/40 border border-border p-6">
                  {/* Countdown Timer */}
                  <div className="mb-4 pb-4 border-b border-border">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Timer className="w-4 h-4 text-red-400" />
                      <span className="text-red-400 text-xs font-bold uppercase tracking-wider">Offer expires in</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 font-mono text-lg text-white font-bold">
                      <span className="bg-red-500/20 border border-red-500/30 px-2 py-1">{String(promoCountdown.hours).padStart(2, '0')}</span>
                      <span className="text-red-400">:</span>
                      <span className="bg-red-500/20 border border-red-500/30 px-2 py-1">{String(promoCountdown.minutes).padStart(2, '0')}</span>
                      <span className="text-red-400">:</span>
                      <span className="bg-red-500/20 border border-red-500/30 px-2 py-1">{String(promoCountdown.seconds).padStart(2, '0')}</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Actual Price</span>
                    <div className="text-2xl text-muted-foreground line-through font-mono">$699</div>
                  </div>
                  <div className="mb-3">
                    <span className="text-xs text-amber-400 uppercase tracking-wider font-bold">Limited Time</span>
                    <div className="text-5xl font-black text-white font-mono">$299</div>
                  </div>
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-green-500/20 text-green-400 text-sm font-bold border border-green-500/30">
                      You Save $400!
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-0.5">+ Complimentary Set Files</div>
                  <div className="text-xs text-green-400 font-medium mb-5">(Normally $100 — FREE with this package)</div>
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 p-3">
                    <p className="text-xs text-amber-400">
                      Send <strong className="text-white">$299 USDT</strong> via BEP20/TRC20 to the payment address below. Include "MT5 BOT" in notes or contact support after payment.
                    </p>
                  </div>
                  
                  <div className="mt-3 text-xs text-muted-foreground">
                    One-time payment &middot; No subscription &middot; Lifetime access
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Or choose a subscription plan</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pricing Card */}
          <div className="bg-card border border-border p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Pro Trader</h2>
                <p className="text-muted-foreground text-sm">Full access to all features</p>
              </div>
            </div>

            {/* Plan Toggle */}
            <div className="flex gap-2 mb-6 p-1 bg-secondary/30 border border-border">
              <button
                onClick={() => setPlanType("monthly")}
                className={`flex-1 py-3 px-4 text-sm font-bold transition-all ${
                  planType === "monthly" 
                    ? "bg-white text-black" 
                    : "text-muted-foreground hover:text-white"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setPlanType("annual")}
                className={`flex-1 py-3 px-4 text-sm font-bold transition-all relative ${
                  planType === "annual" 
                    ? "bg-white text-black" 
                    : "text-muted-foreground hover:text-white"
                }`}
              >
                Annual
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-black text-xs font-bold">
                  SAVE 15%
                </span>
              </button>
            </div>

            {/* Price Display */}
            <div className="text-center mb-6 p-4 bg-secondary/20 border border-border">
              {planType === "annual" ? (
                <>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-2xl text-muted-foreground line-through">${pricing?.monthly * 12 || 360}</span>
                    <span className="text-4xl font-mono font-bold text-green-400" data-numeric>${pricing?.annual || 306}</span>
                  </div>
                  <div className="text-muted-foreground text-sm">/year</div>
                  <div className="mt-2 inline-block px-3 py-1 bg-green-500/20 text-green-400 text-sm font-bold">
                    You save ${pricing?.annual_savings || 54}!
                  </div>
                </>
              ) : (
                <>
                  {isFirstPurchase && pricing?.first_purchase_price ? (
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="text-2xl text-muted-foreground line-through">${pricing?.monthly || 30}</span>
                      <span className="text-4xl font-mono font-bold text-green-400" data-numeric>${pricing?.first_purchase_price}</span>
                    </div>
                  ) : (
                    <div className="text-4xl font-mono font-bold" data-numeric>${pricing?.monthly || 30}</div>
                  )}
                  <div className="text-muted-foreground text-sm">/month</div>
                  {isFirstPurchase && pricing?.first_purchase_price && (
                    <div className="mt-2 inline-block px-3 py-1 bg-green-500/20 text-green-400 text-sm font-bold">
                      30% OFF First Month!
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Annual Savings Calculator */}
            {planType === "monthly" && !isFirstPurchase && (
              <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/30">
                <p className="text-sm text-amber-400">
                  💡 <strong>Switch to Annual</strong> and save <strong>${pricing?.annual_savings || 54}/year</strong> (15% off)
                </p>
              </div>
            )}

            <div className="space-y-3 mb-6">
              {[
                { icon: <Zap className="w-4 h-4" />, text: "Unlimited signal generations" },
                { icon: <TrendingUp className="w-4 h-4" />, text: "All 5 instruments" },
                { icon: <Clock className="w-4 h-4" />, text: "All timeframes (5M, 15M, 30M, 1H)" },
                { icon: <Shield className="w-4 h-4" />, text: "Risk Mode toggle" },
                { icon: <Check className="w-4 h-4" />, text: "Confidence level analysis" },
                { icon: <Check className="w-4 h-4" />, text: "Trade history logging" },
                { icon: <Check className="w-4 h-4" />, text: "Priority support" }
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="text-green-500">{feature.icon}</div>
                  <span className="text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-card border border-border p-8">
            <h3 className="text-xl font-bold mb-6">Pay with USDT</h3>
            
            {isSubscribed ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-bold mb-2">You're Subscribed!</h4>
                <p className="text-muted-foreground text-sm mb-4">
                  Your Pro subscription is active until {user?.subscription_end_date ? new Date(user.subscription_end_date).toLocaleDateString() : "N/A"}
                </p>
                <Link to="/dashboard">
                  <Button className="bg-white text-black hover:bg-gray-200 rounded-none">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Step Indicator */}
                <div className="flex items-center justify-between mb-8">
                  {[
                    { num: 1, label: "Network", icon: Wallet },
                    { num: 2, label: "Send", icon: Send },
                    { num: 3, label: "Confirm", icon: Hash }
                  ].map((step, idx) => (
                    <div key={step.num} className="flex items-center">
                      <div className={`flex flex-col items-center ${paymentStep >= step.num ? "text-white" : "text-muted-foreground"}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                          paymentStep > step.num 
                            ? "bg-green-500 border-green-500" 
                            : paymentStep === step.num 
                              ? "border-white bg-white/10" 
                              : "border-muted-foreground/30"
                        }`}>
                          {paymentStep > step.num ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <step.icon className="w-5 h-5" />
                          )}
                        </div>
                        <span className="text-xs mt-1">{step.label}</span>
                      </div>
                      {idx < 2 && (
                        <div className={`w-12 h-0.5 mx-2 ${paymentStep > step.num ? "bg-green-500" : "bg-muted-foreground/30"}`} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Step 1: Select Network */}
                {paymentStep === 1 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-sm font-bold mb-2 block">Select Network</Label>
                      <Select value={network} onValueChange={setNetwork}>
                        <SelectTrigger className="h-12 rounded-none border-border bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TRC20">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>
                              USDT (TRC-20) - Lower fees
                            </span>
                          </SelectItem>
                          <SelectItem value="BEP20">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                              USDT (BEP-20) - Binance Smart Chain
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="bg-secondary/30 border border-border p-4">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-white">Amount:</strong> ${getDisplayPrice().price} USDT 
                        {planType === "annual" ? " (Annual)" : " (Monthly)"}
                        {getDisplayPrice().original && (
                          <span className="ml-2 text-green-400">
                            (Save ${getDisplayPrice().original - getDisplayPrice().price}!)
                          </span>
                        )}
                      </p>
                    </div>

                    <Button
                      onClick={() => setPaymentStep(2)}
                      className="w-full h-12 bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-wider font-bold"
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Step 2: Send Payment */}
                {paymentStep === 2 && (
                  <div className="space-y-6">
                    <div className="bg-amber-500/10 border border-amber-500/30 p-4">
                      <p className="text-sm text-amber-400 font-bold mb-1">⚠️ Important</p>
                      <p className="text-xs text-muted-foreground">
                        Send exactly <strong className="text-white">${getDisplayPrice().price} USDT</strong> using <strong className="text-white">{network}</strong> network. Double-check the address before sending.
                      </p>
                    </div>

                    <div>
                      <Label className="text-sm font-bold mb-2 block">
                        Send USDT ({network}) to this address:
                      </Label>
                      <div className="bg-secondary/50 border border-border p-4">
                        <div className="flex items-center justify-between gap-2">
                          <code className="text-xs sm:text-sm font-mono break-all text-green-400">
                            {currentAddress || "Loading..."}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(currentAddress)}
                            className="flex-shrink-0"
                            data-testid="copy-address-btn"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <a
                          href={network === "BEP20" 
                            ? `https://bscscan.com/address/${currentAddress}` 
                            : `https://tronscan.org/#/address/${currentAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-white flex items-center gap-1"
                        >
                          View on {network === "BEP20" ? "BSCScan" : "TronScan"} <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setPaymentStep(1)}
                        className="flex-1 h-12 rounded-none border-border"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                      </Button>
                      <Button
                        onClick={() => setPaymentStep(3)}
                        className="flex-1 h-12 bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-wider font-bold"
                      >
                        I've Sent Payment <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Enter Transaction ID */}
                {paymentStep === 3 && (
                  <div className="space-y-6">
                    <div className="bg-blue-500/10 border border-blue-500/30 p-4">
                      <p className="text-sm text-blue-400 font-bold mb-1">📋 Enter Transaction ID</p>
                      <p className="text-xs text-muted-foreground">
                        After your payment is sent, paste the transaction ID (hash) below so we can verify and activate your subscription.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="txHash" className="text-sm font-bold mb-2 block">
                        Transaction ID / Hash
                      </Label>
                      <Input
                        id="txHash"
                        type="text"
                        placeholder={network === "BEP20" ? "0x..." : "Enter TRX hash..."}
                        value={txHash}
                        onChange={(e) => setTxHash(e.target.value)}
                        className="h-12 rounded-none border-border bg-background font-mono text-sm"
                        data-testid="tx-hash-input"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Find this in your wallet's transaction history or on{" "}
                        <a 
                          href={network === "BEP20" ? "https://bscscan.com" : "https://tronscan.org"} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          {network === "BEP20" ? "BSCScan" : "TronScan"}
                        </a>
                      </p>
                    </div>

                    {/* Verification Result */}
                    {verificationResult && (
                      <div className={`p-4 border ${
                        verificationResult.status === "confirmed"
                          ? "bg-green-500/10 border-green-500/30"
                          : verificationResult.status === "pending"
                            ? "bg-amber-500/10 border-amber-500/30"
                            : "bg-red-500/10 border-red-500/30"
                      }`}>
                        <div className="flex items-start gap-3">
                          {verificationResult.status === "confirmed" ? (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          ) : verificationResult.status === "pending" ? (
                            <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          )}
                          <div>
                            <p className={`text-sm font-bold ${
                              verificationResult.status === "confirmed"
                                ? "text-green-400"
                                : verificationResult.status === "pending"
                                  ? "text-amber-400"
                                  : "text-red-400"
                            }`}>
                              {verificationResult.status === "confirmed" 
                                ? "Payment Confirmed! 🎉" 
                                : verificationResult.status === "pending"
                                  ? "Payment Submitted - Awaiting Approval"
                                  : "Verification Failed"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {verificationResult.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setPaymentStep(2)}
                        className="flex-1 h-12 rounded-none border-border"
                        disabled={submitting}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                      </Button>
                      <Button
                        onClick={handleSubmitPayment}
                        disabled={submitting || !txHash.trim()}
                        className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white rounded-none uppercase tracking-wider font-bold"
                        data-testid="verify-payment-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Confirm Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Blockchain Explorer Links */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-muted-foreground mb-2">Check your transaction:</p>
                  <div className="flex justify-center gap-4">
                    {network === "TRC20" ? (
                      <a
                        href={`https://tronscan.org/#/transaction/${txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                      >
                        TronScan <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <a
                        href={`https://bscscan.com/tx/${txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                      >
                        BSCScan <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Payoneer Coming Soon */}
        <div className="mt-8 bg-card border border-dashed border-border p-6 text-center">
          <h3 className="font-bold mb-2">Payoneer Payments Coming Soon</h3>
          <p className="text-sm text-muted-foreground">
            We're integrating Payoneer for additional payment options. Stay tuned!
          </p>
        </div>

        {/* FAQ */}
        <div className="mt-12 space-y-6">
          <h3 className="text-xl font-bold">FAQ</h3>
          
          <div className="space-y-4">
            <div className="bg-card border border-border p-4">
              <h4 className="font-bold mb-2">How long does verification take?</h4>
              <p className="text-sm text-muted-foreground">
                Automatic verification usually takes 1-5 minutes after your transaction is confirmed on the blockchain.
              </p>
            </div>
            
            <div className="bg-card border border-border p-4">
              <h4 className="font-bold mb-2">What if my payment isn't detected?</h4>
              <p className="text-sm text-muted-foreground">
                Please ensure you sent the correct amount to the correct address. If issues persist, contact support with your transaction hash.
              </p>
            </div>
            
            <div className="bg-card border border-border p-4">
              <h4 className="font-bold mb-2">How will I know when my subscription expires?</h4>
              <p className="text-sm text-muted-foreground">
                You'll receive an email reminder 3 days before your subscription expires.
              </p>
            </div>
          </div>
        </div>

        {/* Referral Program Section */}
        <div className="mt-12 border-t border-border pt-8">
          <div className="flex items-center gap-3 mb-6">
            <Gift className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-bold">Referral Program</h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Referral Link */}
            <div className="bg-card border border-border p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Your Referral Link
              </h3>
              {referralStats ? (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={referralStats.referral_link}
                      readOnly
                      className="h-12 rounded-none bg-secondary/50 border-border font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={copyReferralLink}
                      className="h-12 px-4 rounded-none"
                      data-testid="copy-referral-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Share this link with friends. When they upgrade to Pro, you earn rewards!
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">Loading...</p>
              )}
            </div>

            {/* Referral Stats */}
            <div className="bg-card border border-border p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Your Stats
              </h3>
              {referralStats ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-secondary/30 p-4 text-center">
                    <div className="text-3xl font-bold text-white">{referralStats.total_referrals}</div>
                    <div className="text-xs text-muted-foreground uppercase">Total Referrals</div>
                  </div>
                  <div className="bg-secondary/30 p-4 text-center">
                    <div className="text-3xl font-bold text-green-500">{referralStats.active_pro_referrals}</div>
                    <div className="text-xs text-muted-foreground uppercase">Active Pro Users</div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Loading...</p>
              )}
            </div>
          </div>

          {/* Rewards */}
          <div className="mt-6 bg-card border border-border p-6">
            <h3 className="font-bold mb-4">Rewards</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className={`p-4 border ${referralStats?.active_pro_referrals >= 10 ? 'border-green-500 bg-green-500/10' : 'border-border bg-secondary/20'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">10 Active Pro Referrals</span>
                  <span className="text-green-500 font-bold">50% OFF</span>
                </div>
                <p className="text-sm text-muted-foreground">Get 50% off your next renewal</p>
                {referralStats?.active_pro_referrals >= 10 && referralStats?.active_pro_referrals < 20 && (
                  <div className="mt-2 text-xs text-green-400">✓ Eligible!</div>
                )}
              </div>
              <div className={`p-4 border ${referralStats?.active_pro_referrals >= 20 ? 'border-green-500 bg-green-500/10' : 'border-border bg-secondary/20'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">20 Active Pro Referrals</span>
                  <span className="text-green-500 font-bold">1 FREE MONTH</span>
                </div>
                <p className="text-sm text-muted-foreground">1 free month + 50% off subsequent</p>
                {referralStats?.active_pro_referrals >= 20 && (
                  <div className="mt-2 text-xs text-green-400">✓ Eligible!</div>
                )}
              </div>
            </div>
            
            {/* Progress bar */}
            {referralStats && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Progress to next reward</span>
                  <span className="font-mono">{referralStats.active_pro_referrals} / {referralStats.next_reward_at}</span>
                </div>
                <div className="h-2 bg-secondary/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${Math.min((referralStats.active_pro_referrals / referralStats.next_reward_at) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Pending Rewards */}
            {referralStats?.pending_rewards?.length > 0 && (
              <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30">
                <h4 className="font-bold text-green-400 mb-3">🎁 Rewards Ready to Claim!</h4>
                {referralStats.pending_rewards.map((reward) => (
                  <div key={reward.reward_id} className="flex items-center justify-between py-2">
                    <span>{reward.description}</span>
                    <Button
                      size="sm"
                      onClick={() => claimReward(reward.reward_id)}
                      disabled={claimingReward}
                      className="bg-green-500 text-black hover:bg-green-400"
                    >
                      {claimingReward ? <Loader2 className="w-4 h-4 animate-spin" /> : "Claim"}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Claimed Rewards */}
            {referralStats?.claimed_rewards?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-bold text-muted-foreground mb-2">Claimed Rewards</h4>
                {referralStats.claimed_rewards.map((reward, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>{reward.type === 'free_month' ? '1 Free Month + 50% off' : '50% Discount'}</span>
                    <span className="text-xs">({new Date(reward.claimed_at).toLocaleDateString()})</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Important Note */}
          <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-400">
              <strong>Important:</strong> Rewards are based on <strong>active</strong> Pro subscriptions from your referrals. 
              If a referred user doesn't renew, they won't count toward your reward threshold.
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <span className="font-semibold text-amber-500/80">Disclaimer:</span> Aurex AI provides algorithmic market analysis, not financial advice. Trading decisions are the sole responsibility of the user. Aurex AI does not guarantee profits and is not liable for any losses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
